import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import Flyout from '../GenericFlyout';
import { OverlayId } from '../types';
import { MenuItemType, MenuItemInterface } from '../Menu/types';
import Menu from '../Menu';
import CustomFlyoutHeader from '../CustomFlyoutHeader';
import useMenuItemChecks, { menuCheckKey, isMenuItemEnabled } from '../AccountFlyout/hooks/useMenuItemCheck';
import { hideOverlay } from 'Client/redux/overlays/actions';
import useTranslator from 'Client/hooks/useTranslator';
import useConfig from 'Client/hooks/useConfig';
import { VictorQaClasses } from 'Common/qaIds';
import { BellaTranslator, Config } from 'Common/appshell_types';

export const createMenuItems = (t: BellaTranslator, config: Config): MenuItemInterface[] => [
    {
        title: t('Account overview'),
        url: config.links.myAccount,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuOverview,
    },
    {
        title: t('My Wishlist'),
        url: config.links.wishList,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuWishlist,
    },
    {
        title: t('My Looks Wishlist'),
        url: config.links.myLooks,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuMyLooks,
    },
    {
        title: t('My Moodboard'),
        url: config.links.moodboard,
        itemType: MenuItemType.ExternalLinkArrowed,
        checkKey: menuCheckKey.Moodboard,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuMoodboard,
    },
    {
        title: t('My orders'),
        url: config.links.myOrders,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuOrders,
    },
    {
        title: t('reviews_Product review'),
        url: config.links.myReviews,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuReviews,
    },
    {
        title: t('My Vouchers'),
        url: config.links.myCoupons,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuVoucher,
    },
    {
        title: t('My credentials'),
        url: config.links.myEditAccount,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
    },
    {
        title: t('My Addresses'),
        url: config.links.myAddress,
        itemType: MenuItemType.ExternalLinkArrowed,
        fullLoginRequired: true,
        className: VictorQaClasses.accountMenuAddress,
    },
    {
        title: t('My newsletter'),
        url: config.links.myNewsletter,
        itemType: MenuItemType.ExternalLinkArrowed,
        className: VictorQaClasses.accountMenuNewletter,
    },
    {
        title: t('My notifications'),
        url: config.links.myNotifications,
        itemType: MenuItemType.ExternalLinkArrowed,
    },
    {
        title: t('Logout'),
        url: config.links.logout,
        itemType: MenuItemType.ExternalLink,
    },
];

export const AccountMenus: FC<{ closeAction?: () => void }> = ({ closeAction = () => {} }) => {
    const t = useTranslator();
    const config = useConfig();
    const itemChecks = useMenuItemChecks(config);
    const menuItems = createMenuItems(t, config).filter(({ checkKey }) => isMenuItemEnabled(itemChecks, checkKey));
    const menuSections = [
        {
            key: 'accountMenu',
            title: '',
            items: menuItems,
        },
    ];

    return <Menu menuSections={menuSections} closeMenuFn={closeAction} />;
};

export const AccountFlyout: React.SFC = () => {
    const t = useTranslator();
    const dispatch = useDispatch();
    const closeFunction = () => dispatch(hideOverlay(OverlayId.ACCOUNT_MENU));

    return (
        <Flyout identifier={OverlayId.ACCOUNT_MENU} hasCustomHeader>
            <>
                <CustomFlyoutHeader
                    shouldHaveBackBtn={false}
                    title={t('My account')}
                    handleCloseBtnClick={closeFunction}
                />
                <AccountMenus closeAction={closeFunction} />
            </>
        </Flyout>
    );
};

export default AccountFlyout;
